import { useId, type ReactNode } from "react";
import { useShowFiltered } from "../lib/settings";

/**
 * Переключатель-строка: крупная область нажатия, название, пояснение и вторичная метка справа (например, «Скрыто: 4»).
 * Под капотом — настоящий checkbox с role="switch": работает клавиатура (Пробел), читалки экрана и формы.
 */
export function Switch({
  checked,
  onChange,
  label,
  description,
  meta,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <label className={`switch-row${checked ? " is-on" : ""}${className ? ` ${className}` : ""}`} htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="switch-input"
        checked={checked}
        aria-describedby={description ? `${id}-d` : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch-track" aria-hidden="true">
        <span className="switch-thumb" />
      </span>
      <span className="switch-text">
        <span className="switch-label">{label}</span>
        {description && (
          <span className="switch-desc" id={`${id}-d`}>
            {description}
          </span>
        )}
      </span>
      {meta != null && meta !== false && <span className="switch-meta">{meta}</span>}
    </label>
  );
}

/**
 * Единая настройка «Показывать отфильтрованные сигналы» (график, сигналы рынка, журнал). Только отображение.
 * hidden — сколько отфильтрованных сигналов сейчас скрыто в этом месте; total — сколько их всего (для режима «показаны»).
 */
export function FilteredSignalsToggle({ total, className }: { total: number; className?: string }) {
  const [on, setOn] = useShowFiltered();
  return (
    <Switch
      className={`filtered-toggle${className ? ` ${className}` : ""}`}
      checked={on}
      onChange={setOn}
      label="Показывать отфильтрованные сигналы"
      description="Сигналы, не прошедшие фильтры тренда или COT"
      meta={
        total > 0 ? (
          <span className={`count-badge${on ? " is-shown" : ""}`} title="Отфильтрованных сигналов за всю загруженную историю">{on ? `Показано: ${total}` : `Скрыто: ${total}`}</span>
        ) : (
          <span className="count-badge is-empty">Нет таких</span>
        )
      }
    />
  );
}

/** Поле выбора: подпись над нативным select со стрелкой, высота 40px, крупный текст. */
export function SelectField<T extends string | number>({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: ReactNode;
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
  className?: string;
}) {
  const id = useId();
  return (
    <div className={`select-field${className ? ` ${className}` : ""}`}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="select"
        value={String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          const hit = options.find(([v]) => String(v) === raw);
          if (hit) onChange(hit[0]);
        }}
      >
        {options.map(([v, t]) => (
          <option key={String(v)} value={String(v)}>
            {t}
          </option>
        ))}
      </select>
    </div>
  );
}
